import { Alert, Button, buttonClass, cardClass, fieldClass, selectClass, textareaClass } from '@ledgerloom/ui';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { propose, type Outcome } from '../api';
import { useSession } from '../session';
import { useApi, usePageTitle } from '../hooks';
import { Breadcrumb, ErrorBanner, Reasons, useToast } from '../components/ui';
import { Field, fieldErrors } from '../components/Field';
import { TagInput } from '../components/TagInput';
import { forgetParty, PartyName } from '../components/PartyName';
import { defaultClass, gradeName, type Fields } from '../contactSettings';

type Kind = 'person' | 'household' | 'organization';

interface FormState {
  kind: Kind;
  given_name: string; family_name: string; org_name: string;
  email: string; phone: string; phone_label: string;
  street: string; city: string; state: string; postal_code: string;
  birthday: string; tags: string[]; grade: string; contact_class: string; note: string;
}

const blank: FormState = {
  kind: 'person', given_name: '', family_name: '', org_name: '', email: '', phone: '', phone_label: 'mobile',
  street: '', city: '', state: '', postal_code: '', birthday: '', tags: [], grade: '', contact_class: '', note: '',
};

type Errors = Partial<Record<keyof FormState | 'name', string>>;

// Checks a person can fix before the gate sees the proposal. The gate still checks everything.
function validate(f: FormState): Errors {
  const e: Errors = {};
  if (f.kind === 'person' && !f.given_name.trim() && !f.family_name.trim()) e.name = 'Enter a first or last name.';
  if (f.kind !== 'person' && !f.org_name.trim()) e.org_name = `Enter the ${f.kind === 'household' ? 'household' : 'organization'} name.`;
  if (f.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim())) e.email = 'Enter an email like name@example.com.';
  if (f.phone.trim() && f.phone.replace(/\D/g, '').length < 7) e.phone = 'Enter the full number, with area code.';
  if (f.state.trim() && !/^[A-Za-z]{2}$/.test(f.state.trim())) e.state = 'Use the two-letter code, like TX.';
  if (f.postal_code.trim() && !/^\d{5}(-?\d{4})?$/.test(f.postal_code.trim())) e.postal_code = 'Use 5 digits, or 5+4.';
  return e;
}

// Gateway schema paths ("/emails_add/0/address", "/set/given_name") to form fields.
const PATH_FIELD: Record<string, keyof FormState | 'name'> = {
  given_name: 'given_name', family_name: 'family_name', org_name: 'org_name', emails: 'email', emails_add: 'email', address: 'email',
  phones: 'phone', phones_add: 'phone', number: 'phone', addresses: 'street', addresses_add: 'street', street: 'street', city: 'city',
  state: 'state', postal_code: 'postal_code', occasions: 'birthday', occasions_add: 'birthday', on_date: 'birthday',
  tags: 'tags', tags_add: 'tags', grade: 'grade', contact_class: 'contact_class', note: 'note',
};
const mapPath = (segs: string[]) => { for (const x of [...segs].reverse()) if (PATH_FIELD[x]) return PATH_FIELD[x]; return undefined; };

// A new contact, sent to the gate as a proposal. An existing contact is edited
// in place on its own page. Patterns: forms/text-field, forms/phone-number,
// forms/tag-input, forms/selection-input, forms/textarea, forms/form-validation,
// and forms/button.
export function ContactNew() {
  const fields = useApi<Fields>('/v1/contact-fields');
  const [f, setF] = useState<FormState>(blank);
  const [error, setError] = useState<unknown>(null);
  const [rejected, setRejected] = useState<Outcome | null>(null);
  const [busy, setBusy] = useState(false);
  const [touched, setTouched] = useState<Set<string>>(new Set());
  const [submitted, setSubmitted] = useState(false);
  const rejectedRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const toast = useToast();
  const { refreshStats } = useSession();

  usePageTitle('New contact');
  const contactClass = f.contact_class || defaultClass(fields.data)?.key || '';
  useEffect(() => { if (rejected) rejectedRef.current?.focus(); }, [rejected]);

  const set = (k: keyof FormState) => (e: { target: { value: string } }) => setF((cur) => ({ ...cur, [k]: e.target.value }));
  const blur = (k: string) => () => setTouched((cur) => new Set(cur).add(k));
  const local = validate(f);
  const server = fieldErrors(error, mapPath);
  // Show a field's own error once it has been left or the form submitted; the server's always.
  const err = (k: keyof Errors) => server[k] ?? ((submitted || touched.has(k)) ? local[k] : undefined) ?? null;
  const contactAdds = () => ({
    emails: f.email.trim() ? [{ address: f.email.trim(), label: 'personal' as const }] : [],
    phones: f.phone.trim() ? [{ number: f.phone.trim(), label: f.phone_label }] : [],
    addresses: f.street || f.city || f.postal_code ? [{ street: f.street || undefined, city: f.city || undefined, state: f.state || undefined, postal_code: f.postal_code || undefined, label: 'home' }] : [],
  });

  async function submit(e: FormEvent) {
    e.preventDefault();
    setSubmitted(true);
    const first = Object.keys(local)[0];
    if (first) { document.getElementById(first === 'name' ? 'given_name' : first)?.focus(); return; }
    setBusy(true); setError(null); setRejected(null);
    try {
      const adds = contactAdds();
      const out: Outcome = await propose('party.create', {
        kind: f.kind,
        ...(f.kind === 'person' ? { given_name: f.given_name || undefined, family_name: f.family_name || undefined } : { org_name: f.org_name }),
        ...(adds.emails.length ? { emails: adds.emails } : {}),
        ...(adds.phones.length ? { phones: adds.phones } : {}),
        ...(adds.addresses.length ? { addresses: adds.addresses } : {}),
        ...(f.birthday ? { occasions: [{ kind: 'birthday', on_date: f.birthday }] } : {}),
        ...(f.tags.length ? { tags: f.tags } : {}),
        ...(f.grade ? { grade: f.grade } : {}),
        ...(contactClass ? { contact_class: contactClass } : {}),
        ...(f.note.trim() ? { note: f.note.trim() } : {}),
        source: { channel: 'Manual entry' },
      });
      refreshStats();
      if (out.state === 'written') {
        const target = out.result?.party_id as string;
        forgetParty(target);
        toast('Contact created');
        navigate(`/contacts/${target}`);
      } else if (out.state === 'pending_approval') {
        toast('Sent for approval. Nothing is saved until a person approves it.');
        navigate('/desk/needs-you');
      } else setRejected(out);
    } catch (err) { setError(err); } finally { setBusy(false); }
  }

  const best = rejected?.computed?.dedup?.best as { party_id: string } | undefined;
  const nameHint = f.kind === 'person' ? 'A first or last name is required.' : undefined;

  return (
    <form className="stack" onSubmit={submit} style={{ maxWidth: 760 }} noValidate>
      <Breadcrumb items={[{ to: '/contacts', label: 'Contacts' }, { label: 'New contact' }]} />
      <div className="page-head"><div><h1>New contact</h1><p>It goes through the gate. Possible duplicates go to a person first. Once made, change anything on the contact's page.</p></div></div>
      <ErrorBanner error={error} focusOnShow />
      {rejected && (
        <Alert tone="danger" title="Not saved" tabIndex={-1} ref={rejectedRef}
          action={best && <Link className={buttonClass({ variant: 'secondary', size: 'sm' })} to={`/contacts/${best.party_id}`}>Open the existing record: <PartyName id={best.party_id} link={false} /></Link>}>
          <Reasons reasons={rejected.reasons} />
        </Alert>
      )}
      {submitted && Object.keys(local).length > 0 && (
        <Alert tone="danger" role="alert">Fix {Object.keys(local).length === 1 ? 'the field' : `the ${Object.keys(local).length} fields`} marked below.</Alert>
      )}

      <fieldset className={`${cardClass} card-pad form-grid`} style={{ margin: 0 }}>
        <legend className="sr-only">Name</legend>
        <Field label="Type" className="full">
          <select id="kind" className={selectClass} value={f.kind} onChange={set('kind')}>
            <option value="person">Person</option><option value="household">Household</option><option value="organization">Organization</option>
          </select>
        </Field>
        {f.kind === 'person' ? (
          <>
            <Field label="First name" hint={nameHint} error={err('name') ?? err('given_name')}>
              <input id="given_name" className={fieldClass} autoComplete="off" value={f.given_name} onChange={set('given_name')} onBlur={blur('name')} autoFocus />
            </Field>
            <Field label="Last name" error={err('family_name')}>
              <input id="family_name" className={fieldClass} autoComplete="off" value={f.family_name} onChange={set('family_name')} onBlur={blur('name')} />
            </Field>
          </>
        ) : (
          <Field label={f.kind === 'household' ? 'Household name' : 'Organization name'} className="full" error={err('org_name')} required>
            <input id="org_name" className={fieldClass} autoComplete="off" value={f.org_name} onChange={set('org_name')} onBlur={blur('org_name')} />
          </Field>
        )}
      </fieldset>

      <fieldset className={`${cardClass} card-pad form-grid`} style={{ margin: 0 }}>
        <legend className="sr-only">Contact details</legend>
        <h2 className="full" aria-hidden="true" style={{ fontSize: 14 }}>Contact details</h2>
        <Field label="Email" className="full" error={err('email')}>
          <input id="email" className={fieldClass} type="email" inputMode="email" autoComplete="off" value={f.email} onChange={set('email')} onBlur={blur('email')} />
        </Field>
        <Field label="Phone" hint="Include the area code." error={err('phone')}>
          <input id="phone" className={fieldClass} type="tel" autoComplete="off" value={f.phone} onChange={set('phone')} onBlur={blur('phone')} />
        </Field>
        <Field label="Phone type">
          <select id="plabel" className={selectClass} value={f.phone_label} onChange={set('phone_label')}><option value="mobile">Mobile</option><option value="home">Home</option><option value="work">Work</option></select>
        </Field>
        <Field label="Street" className="full" error={err('street')}>
          <input id="street" className={fieldClass} autoComplete="off" value={f.street} onChange={set('street')} />
        </Field>
        <Field label="City" error={err('city')}>
          <input id="city" className={fieldClass} autoComplete="off" value={f.city} onChange={set('city')} />
        </Field>
        <div className="row" style={{ gap: 14, alignItems: 'flex-start' }}>
          <Field label="State" error={err('state')} className="grow">
            <input id="state" className={fieldClass} maxLength={2} autoComplete="off" value={f.state} onChange={set('state')} onBlur={blur('state')} />
          </Field>
          <Field label="ZIP" error={err('postal_code')} className="grow">
            <input id="postal_code" className={fieldClass} inputMode="numeric" autoComplete="off" value={f.postal_code} onChange={set('postal_code')} onBlur={blur('postal_code')} />
          </Field>
        </div>
        {f.kind === 'person' && (
          <Field label="Birthday" error={err('birthday')}>
            <input id="birthday" className={fieldClass} type="date" value={f.birthday} onChange={set('birthday')} />
          </Field>
        )}
      </fieldset>

      <fieldset className={`${cardClass} card-pad form-grid`} style={{ margin: 0 }}>
        <legend className="sr-only">Class, grade, tags, and note</legend>
        <Field label="Class" hint="It decides the lifecycle the contact moves through." error={err('contact_class')}>
          <select id="contact_class" className={selectClass} value={contactClass} onChange={set('contact_class')} disabled={!fields.data}>
            {(fields.data?.classes ?? []).map((c) => <option key={c.key} value={c.key}>{c.name}{c.is_default ? ' (default)' : ''}</option>)}
          </select>
        </Field>
        <Field label="Tags" hint="Press Enter or comma after each tag." error={err('tags')}>
          <TagInput id="tags" value={f.tags} onChange={(tags) => setF((cur) => ({ ...cur, tags }))} placeholder="buyer, sphere" />
        </Field>
        <Field label="Grade" hint="Highest first, as your workspace defines them." error={err('grade')}>
          <select id="grade" className={selectClass} value={f.grade} onChange={set('grade')}>
            <option value="">None</option>
            {(fields.data?.grades ?? []).map((g) => <option key={g.key} value={g.key}>{gradeName(fields.data, g.key)}</option>)}
          </select>
        </Field>
        <Field label="Note" className="full" hint="Saved as plain text." error={err('note')}>
          <textarea id="note" className={textareaClass} value={f.note} onChange={set('note')} />
        </Field>
      </fieldset>

      <div className="row">
        <Button type="submit" disabled={busy}>{busy ? 'Saving…' : 'Create contact'}</Button>
        <Link className={buttonClass({ variant: 'ghost' })} to="/contacts">Cancel</Link>
      </div>
    </form>
  );
}
